'use client';

import { c as _c } from "react/compiler-runtime";
import type { Middleware, MPAInterceptedResponse } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { useMemo, type PropsWithChildren } from 'react';
import { NonProjectDalContext } from '@/contexts/nonProjectDalContext';
import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { createHttpClient, refreshTokenIfNeeded } from '@/data/dal/httpClient';
import { createMpaMiddleware } from '@/data/dal/mpaMiddleware';
import { createConfig } from './api-config';
import type { Environment } from './environment';
import { useEnvironment } from './useEnvironment';
const createMiddleware = (authToken: string, updateAuthToken: (token: string) => void, environment: Environment, serverFlags: string | null): Middleware => {
  const useRefreshToken = !(environment.isDemo || environment.isDev || environment.isTest) || environment.enableForceLogoutOn401;
  let refreshPromise = Promise.resolve(authToken);
  let isRefreshing = false;
  const refreshToken = () => {
    if (!useRefreshToken || isRefreshing) {
      return refreshPromise;
    }
    isRefreshing = true;
    refreshPromise = refreshTokenIfNeeded(authToken, environment.clientApiBaseUrl, updateAuthToken).then(token => {
      isRefreshing = false;
      return token;
    }, () => {
      isRefreshing = false;
      return authToken;
    });
    return refreshPromise;
  };
  return {
    pre: async context => {
      if (serverFlags) {
        context.setHeaderParam('X-Server-Flags', serverFlags);
      }
      if (!context.getUrl().endsWith('/refresh')) {
        const newAuthToken = await refreshToken();
        context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
      }
      return context;
    },
    post: async context => {
      if (useRefreshToken && context.httpStatusCode === 401) {
        // The user is not authenticated, redirect to login
        location.href = '/logout';
      }
      return context;
    }
  };
};
interface NonProjectDalProviderProps {
  // Injected from the UI layer so the contexts module stays free of any
  // dialog/UI imports. Building the handler requires WorkspaceProvider, which
  // mounts above this provider — see `useMpaInterceptHandler`.
  mpaInterceptHandler: (payload: MPAInterceptedResponse) => Promise<void>;
}
export const NonProjectDalProvider = t0 => {
  const $ = _c(19);
  const {
    children,
    mpaInterceptHandler
  } = t0;
  const env = useEnvironment();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("serverFlags");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const serverFlags = t1;
  let config;
  if ($[2] !== env || $[3] !== mpaInterceptHandler || $[4] !== serverFlags) {
    config = createConfig(env.clientApiBaseUrl, env.authToken);
    config.middleware.push(createMiddleware(env.authToken, env.updateAuthToken, env, serverFlags), createMpaMiddleware(mpaInterceptHandler));
    $[2] = env;
    $[3] = mpaInterceptHandler;
    $[4] = serverFlags;
    $[5] = config;
  } else {
    config = $[5];
  }
  const configuration = config;
  let t2;
  if ($[6] !== env) {
    t2 = createHttpClient(env.authToken, env.updateAuthToken, env);
    $[6] = env;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const httpClient = t2;
  const t3 = env.isDev || env.isTest;
  let t4;
  if ($[8] !== configuration || $[9] !== httpClient || $[10] !== t3) {
    t4 = nonProjectDalFactory(httpClient, configuration, t3);
    $[8] = configuration;
    $[9] = httpClient;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const nonProjectDal = t4;
  let t5;
  if ($[12] !== configuration || $[13] !== httpClient || $[14] !== nonProjectDal) {
    t5 = {
      nonProjectDal,
      httpClient,
      configuration
    };
    $[12] = configuration;
    $[13] = httpClient;
    $[14] = nonProjectDal;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== children || $[17] !== t5) {
    t6 = <NonProjectDalContext.Provider value={t5}>{children}</NonProjectDalContext.Provider>;
    $[16] = children;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};